import axios from 'axios';
import { BASE_URL } from '../constants/constants';
import { getApplicantToken } from '../utils/applicantToken';
import { linkProblemFromResponse, reportLinkProblem } from '../utils/linkProblem';

/**
 * Every applicant-facing call. The applicant's invite-link token rides along as the raw
 * `Authorization` header (no "Bearer"): the API resolves the applicant from it and refuses ids
 * in the body or path that aren't that applicant's.
 */
export const applicantApi = axios.create({ baseURL: BASE_URL });

applicantApi.interceptors.request.use(config => {
  const token = getApplicantToken();
  // An explicit header wins: getApplicantFromToken sends the URL's token itself.
  if (token && !config.headers.Authorization) config.headers.Authorization = token;
  return config;
});

applicantApi.interceptors.response.use(undefined, (error: unknown) => {
  // A dead link is reported to the verification container (which swaps in the "link no longer
  // valid" screen) AND still rejected, so the caller's own failure handling runs as before.
  if (axios.isAxiosError(error)) {
    const problem = linkProblemFromResponse(error.response?.status, error.response?.data);
    if (problem) reportLinkProblem(problem);
  }
  return Promise.reject(error);
});

/**
 * Calls scoped by their own token — the employer VOE page sends the employer's. Kept off
 * applicantApi so an applicant token left in this tab can never be attached to them.
 */
export const publicApi = axios.create({ baseURL: BASE_URL });

/** The API's `message` for a failed call (5xx messages are generic), or `fallback`. */
export const apiErrorMessage = (err: unknown, fallback: string): string => {
  const message = axios.isAxiosError(err) ? (err.response?.data as { message?: unknown } | undefined)?.message : undefined;
  return typeof message === 'string' && message ? message : fallback;
};
